import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pbLevelIndex } from '@/data/mockSafeData';
import type { FundamentalCatalogItem, ActionItem, TipItem } from '@/api/types';

export interface KudosCarouselProps {
  strengths:   { id: string; avg: number; count: number }[];
  t:           (key: string) => string;
  catalogData: FundamentalCatalogItem[];
  actionsData: ActionItem[];
  tipsData:    TipItem[];
}

/** Renders an icon from the catalog: FA class name or uploaded image URL. */
function FundIcon({ icon }: { icon?: string }) {
  if (!icon)               return <i className="fas fa-circle" />;
  if (icon.startsWith('fa-')) return <i className={`fas ${icon}`} />;
  return <img src={icon} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />;
}

export function KudosCarousel({ strengths, t, catalogData, actionsData, tipsData }: KudosCarouselProps) {
  const { i18n } = useTranslation();
  const lang  = i18n.language.split('-')[0];
  const [slide, setSlide] = useState(0);
  const total = strengths.length;

  const k = strengths[slide];
  if (!k) return null;

  const fund  = catalogData.find(f => f.id === k.id);
  const name  = fund?.shortNames?.[lang] ?? fund?.shortName ?? fund?.names?.[lang] ?? k.id;
  const icon  = fund?.icon;
  const color = fund?.color ?? '#342F82';

  const lvl    = pbLevelIndex(k.avg);
  const lvlBg   = ['#FEE2E2', '#FEF3C7', '#E0F2FE', '#D1FAE5'];
  const lvlText = ['#991B1B', '#92400E', '#0369A1', '#065F46'];

  const action = actionsData.find(a =>
    a.fundamentalId === k.id && a.active &&
    k.avg >= a.threshold && k.avg <= a.thresholdMax,
  ) ?? actionsData.find(a => a.fundamentalId === k.id && a.active);

  const recTip = tipsData.find(tip =>
    tip.fundamentalId === k.id && tip.tipType === 'reconocer' && tip.active,
  );
  const quote = recTip
    ? (recTip.translations?.[lang] ?? recTip.tipText)
    : '';

  return (
    <div className="ud-card" style={{ marginTop: 16 }}>
      <div className="ud-section-head">
        <div className="ud-icon-box green"><i className="fas fa-heart" /></div>
        <div>
          <h3>{t('safe.kudosTitle')}</h3>
          <small>{t('safe.kudosSub')}</small>
        </div>
        {total > 1 && (
          <div className="ud-cs-nav" style={{ marginLeft: 'auto', flexShrink: 0 }}>
            <button
              type="button"
              className="ud-cs-btn"
              onClick={() => setSlide((slide - 1 + total) % total)}
            >
              <i className="fas fa-chevron-left" />
            </button>
            <span className="ud-cs-counter">{slide + 1} / {total}</span>
            <button
              type="button"
              className="ud-cs-btn"
              onClick={() => setSlide((slide + 1) % total)}
            >
              <i className="fas fa-chevron-right" />
            </button>
          </div>
        )}
      </div>

      <div className="ud-carousel">
        <div className="ud-cs-slide active">
          <div className="ud-cs-top">
            <div className="ud-cs-icon" style={{ color }}>
              <FundIcon icon={icon} />
            </div>
            <div className="ud-cs-name">
              {name}
              <span
                className="ud-cs-badge"
                style={{ background: lvlBg[lvl], color: lvlText[lvl] }}
              >
                {t(`safe.level${lvl}`)}
              </span>
            </div>
          </div>

          <div className="ud-cs-peers">
            {k.count} {t('safe.popup.peersOpined')} · {t('safe.thisMonth')}
          </div>

          <div className="ud-cs-pills">
            {quote && (
              <div className="ud-cs-pill reconocer">
                <div className="ud-cs-pill-label">
                  <i className="fas fa-compass" /> {t('survey.tips.reconocer').toUpperCase()}
                </div>
                <div className="ud-cs-pill-text">"{quote}"</div>
              </div>
            )}
            <div className="ud-cs-pill crecer">
              <div className="ud-cs-pill-label">
                <i className="fas fa-seedling" /> {t('survey.tips.crecer').toUpperCase()}
              </div>
              <div className="ud-cs-pill-text">
                {action
                  ? `${action.translations?.[lang]?.title ?? action.title}: ${action.translations?.[lang]?.description ?? action.description}`
                  : '—'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {total > 1 && (
        <div className="ud-cs-dots">
          {Array.from({ length: total }, (_, i) => (
            <span
              key={`dot-${i}`}
              className={`ud-cs-dot${i === slide ? ' active' : ''}`}
              onClick={() => setSlide(i)}
              role="button"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter') setSlide(i); }}
              aria-label={`${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
